import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthForm } from "../components/auth-form";
import { AuthShell } from "../components/auth-shell";
import { useAuthCtx } from "../lib/auth-provider";
import { linkWixContact } from "../lib/callables";
import { decideAndRedirect } from "../lib/post-auth";

export function SignupPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuthCtx();
  const { t } = useTranslation();
  // Store the i18n KEY (not the translated string) so the toggle EN|ES
  // re-renders the error in the current language.
  const [ssoErrorKey, setSsoErrorKey] = useState<string | null>(null);

  const wixContactId = params.get("wixContactId");
  const loginHref = `/login${params.toString() ? "?" + params.toString() : ""}`;

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    // Fresh signup with email/password leaves emailVerified=false; route the
    // user through /verify-email so they can claim the trial. Google sign-in
    // returns emailVerified=true on first login and skips the gate.
    if (!user.emailVerified) {
      const q = params.toString();
      nav(`/verify-email${q ? "?" + q : ""}`, { replace: true });
      return;
    }
    decideAndRedirect({
      user,
      params,
      nav,
      onSsoError: () => setSsoErrorKey("auth.errors.ssoMintFailed"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  async function onSuccess() {
    if (wixContactId) {
      try { await linkWixContact({ wixContactId }); }
      catch (e) { console.warn("linkWixContact failed", e); }
    }
    // useEffect above will pick up the auth state change and route us.
  }

  if (loading) return null;
  if (user && !ssoErrorKey) return null;

  return (
    <AuthShell
      formTitle={t("auth.shell.signupTitle")}
      formSub={t("auth.shell.signupSub")}
      footer={<>{t("auth.shell.signupFooter")}<Link to={loginHref}>{t("auth.shell.signupFooterLink")}</Link></>}
    >
      {ssoErrorKey && (
        <div className="acx__alert acx__alert--error" style={{ marginBottom: "1rem" }}>
          {t(ssoErrorKey)}
        </div>
      )}
      {!user && <AuthForm mode="signup" onSuccess={onSuccess} />}
    </AuthShell>
  );
}
